import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Target, Loader2, Search, CalendarDays, Users, Hash, Trash2,
  ExternalLink, FileText, Clock, CheckCircle2, AlertTriangle, Archive,
} from 'lucide-react';
import { toast } from 'sonner';

// ----- filter tabs -----
const FILTERS = [
  { key: 'all',               label: 'All Campaigns' },
  { key: 'ongoing_scheduled', label: 'Ongoing Scheduled' },
  { key: 'completed',         label: 'Completed' },
  { key: 'draft',             label: 'Draft' },
  { key: 'failed',            label: 'Failed' },
];

const STATUS_META = {
  ongoing_scheduled: { label: 'Ongoing Scheduled', cls: 'bg-sky-50 text-sky-700 border-sky-200', icon: Clock },
  completed:         { label: 'Completed',         cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  draft:             { label: 'Draft',             cls: 'bg-zinc-100 text-zinc-700 border-zinc-200', icon: FileText },
  failed:            { label: 'Failed',            cls: 'bg-red-50 text-red-700 border-red-200', icon: AlertTriangle },
  empty:             { label: 'No posts',          cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: Archive },
};

const POST_STATUS_META = {
  draft:      { cls: 'bg-zinc-100 text-zinc-700', dot: 'bg-zinc-400' },
  scheduled:  { cls: 'bg-sky-50 text-sky-700',    dot: 'bg-sky-500' },
  published:  { cls: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
  failed:     { cls: 'bg-red-50 text-red-700',    dot: 'bg-red-500' },
  publishing: { cls: 'bg-indigo-50 text-indigo-700', dot: 'bg-indigo-500' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const truncate = (s, n = 80) => {
  if (!s) return '';
  return s.length > n ? s.slice(0, n) + '…' : s;
};

export const CampaignsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/campaigns');
      setCampaigns(r.data.campaigns || []);
    } catch {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => {
    const c = { all: campaigns.length, ongoing_scheduled: 0, completed: 0, draft: 0, failed: 0 };
    campaigns.forEach((x) => {
      const s = x.campaign_status;
      if (s && c[s] != null) c[s] += 1;
    });
    return c;
  }, [campaigns]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (filter !== 'all' && c.campaign_status !== filter) return false;
      if (q && !(c.prompt || '').toLowerCase().includes(q) && !(c.audience || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [campaigns, filter, search]);

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete campaign "${truncate(c.prompt, 40)}"? Its non-published posts will be unlinked as individual drafts.`)) return;
    try {
      await api.delete(`/campaigns/${c.id}`);
      toast.success('Campaign deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <Target className="w-6 h-6 text-indigo-600" />
            Campaign Manager
          </h1>
          <p className="text-zinc-500 text-sm mt-0.5">
            Every campaign generated from AI Post Manager lives here. Track status, open post lists, or clean up finished runs.
          </p>
        </div>
        <Button
          onClick={() => navigate('/ai-manager')}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="new-campaign-btn"
        >
          New campaign
        </Button>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          const count = counts[f.key] ?? 0;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                active
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'
              }`}
              data-testid={`campaign-filter-${f.key}`}
            >
              {f.label} <span className={active ? 'text-white/80' : 'text-zinc-400'}>· {count}</span>
            </button>
          );
        })}
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Search prompt or audience…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 w-[260px] h-9 text-sm"
            data-testid="campaign-search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-zinc-200">
          <CardContent className="p-12 text-center">
            <Target className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-zinc-700">No campaigns to show</p>
            <p className="text-xs text-zinc-500 mt-1 max-w-md mx-auto">
              {campaigns.length === 0
                ? 'Create your first campaign from AI Post Manager and it will appear here automatically.'
                : `No campaigns match "${filter === 'all' ? search : filter}". Adjust the filters above.`}
            </p>
            {campaigns.length === 0 && (
              <Button onClick={() => navigate('/ai-manager')} className="mt-4">
                Go to AI Post Manager
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((c) => {
            const meta = STATUS_META[c.campaign_status] || STATUS_META.empty;
            const StatusIcon = meta.icon;
            return (
              <Card
                key={c.id}
                className="border-zinc-200 hover:shadow-md transition-shadow overflow-hidden cursor-pointer"
                onClick={() => setDetailId(c.id)}
                data-testid={`campaign-card-${c.id}`}
              >
                <div className="h-1.5 bg-indigo-500" />
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-zinc-900 line-clamp-2">
                      {truncate(c.prompt, 90) || 'Untitled campaign'}
                    </h3>
                    <Badge variant="outline" className={`${meta.cls} text-[10px] whitespace-nowrap shrink-0`}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {meta.label}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 pt-0">
                  {c.audience && (
                    <div className="flex items-start gap-1.5 text-xs text-zinc-600">
                      <Users className="w-3.5 h-3.5 mt-0.5 text-zinc-400 shrink-0" />
                      <span className="line-clamp-2">{c.audience}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 text-xs text-zinc-600">
                    <CalendarDays className="w-3.5 h-3.5 text-zinc-400" />
                    <span>{fmtDate(c.start_date)} → {fmtDate(c.end_date)}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(c.platforms || []).slice(0, 4).map((p) => (
                      <span
                        key={p}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700"
                      >
                        {p}
                      </span>
                    ))}
                    {(c.platforms || []).length > 4 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">
                        +{c.platforms.length - 4}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-zinc-500 pt-1 border-t border-zinc-100">
                    <span className="flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      {c.post_count || 0} posts
                    </span>
                    <div className="flex items-center gap-1.5">
                      {c.draft_count > 0 && <span>{c.draft_count}d</span>}
                      {c.scheduled_count > 0 && <span className="text-sky-700">{c.scheduled_count}s</span>}
                      {c.published_count > 0 && <span className="text-emerald-700">{c.published_count}p</span>}
                      {c.failed_count > 0 && <span className="text-red-700">{c.failed_count}f</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs flex-1"
                      onClick={(e) => { e.stopPropagation(); navigate(`/posts?campaign=${c.id}`); }}
                      data-testid={`view-posts-${c.id}`}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Posts
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={(e) => { e.stopPropagation(); handleDelete(c); }}
                      data-testid={`delete-campaign-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail dialog */}
      <CampaignDetailDialog
        campaignId={detailId}
        open={!!detailId}
        onOpenChange={(v) => { if (!v) setDetailId(null); }}
        onDeleted={() => { setDetailId(null); load(); }}
      />
    </div>
  );
};

// ----- Detail dialog -----
const CampaignDetailDialog = ({ campaignId, open, onOpenChange, onDeleted }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!campaignId || !open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [cRes, pRes] = await Promise.all([
          api.get(`/campaigns/${campaignId}`),
          api.get(`/posts?campaign_id=${campaignId}&limit=200`),
        ]);
        if (!cancelled) {
          setCampaign(cRes.data);
          setPosts(pRes.data.posts || []);
        }
      } catch {
        toast.error('Failed to load campaign');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId, open, api]);

  const grouped = useMemo(() => {
    const out = { draft: [], scheduled: [], published: [], failed: [], archived: [] };
    posts.forEach((p) => {
      if (p.archived) out.archived.push(p);
      else if (p.status && out[p.status]) out[p.status].push(p);
      else out.draft.push(p);
    });
    return out;
  }, [posts]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="campaign-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-indigo-600" />
            Campaign details
          </DialogTitle>
          <DialogDescription>
            Full prompt, target audience, platforms, and every post tied to this campaign.
          </DialogDescription>
        </DialogHeader>

        {loading || !campaign ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
          </div>
        ) : (
          <ScrollArea className="flex-1 pr-3">
            <div className="space-y-4">
              {/* Meta block */}
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <Field label="Prompt">{campaign.prompt || '—'}</Field>
                <Field label="Target audience">{campaign.audience || '—'}</Field>
                <Field label="Tone">{campaign.tone || '—'}</Field>
                <Field label="Posts per day">{campaign.posts_per_day || '—'}</Field>
                <Field label="Start date">{fmtDate(campaign.start_date)}</Field>
                <Field label="End date">{fmtDate(campaign.end_date)}</Field>
                <Field label="Platforms" span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(campaign.platforms || []).map((p) => (
                      <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>
                    ))}
                  </div>
                </Field>
              </div>

              {/* Status summary */}
              <div className="grid grid-cols-4 gap-2 text-xs">
                <Stat label="Draft" value={campaign.draft_count} tint="bg-zinc-50 text-zinc-700" />
                <Stat label="Scheduled" value={campaign.scheduled_count} tint="bg-sky-50 text-sky-700" />
                <Stat label="Published" value={campaign.published_count} tint="bg-emerald-50 text-emerald-700" />
                <Stat label="Failed" value={campaign.failed_count} tint="bg-red-50 text-red-700" />
              </div>

              {/* Posts grouped */}
              <div className="space-y-3">
                {['scheduled', 'draft', 'published', 'failed', 'archived'].map((bucket) => {
                  const rows = grouped[bucket];
                  if (!rows?.length) return null;
                  return (
                    <div key={bucket}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${POST_STATUS_META[bucket]?.dot || 'bg-zinc-400'}`} />
                        <h4 className="text-xs font-semibold text-zinc-700 uppercase tracking-wide">
                          {bucket} <span className="text-zinc-400">({rows.length})</span>
                        </h4>
                      </div>
                      <div className="space-y-1.5">
                        {rows.slice(0, 8).map((p) => (
                          <div
                            key={p.id}
                            className="flex items-center gap-2 text-xs px-2 py-1.5 rounded border border-zinc-100 hover:bg-zinc-50"
                            data-testid={`campaign-post-${p.id}`}
                          >
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 shrink-0">
                              {p.platform}
                            </span>
                            <span className="truncate flex-1">
                              {truncate(p.post_title || p.caption, 70) || '(empty)'}
                            </span>
                            <span className="text-[10px] text-zinc-400 shrink-0">
                              {p.scheduled_for ? fmtDate(p.scheduled_for) : ''}
                            </span>
                          </div>
                        ))}
                        {rows.length > 8 && (
                          <button
                            className="text-[11px] text-indigo-600 hover:underline"
                            onClick={() => navigate(`/posts?campaign=${campaign.id}`)}
                          >
                            View all {rows.length} {bucket} posts →
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {posts.length === 0 && (
                  <p className="text-xs text-zinc-500 text-center py-6">No posts yet in this campaign.</p>
                )}
              </div>
            </div>
          </ScrollArea>
        )}

        <div className="flex items-center justify-between gap-2 pt-3 border-t border-zinc-100">
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={async () => {
              if (!campaign || !window.confirm(`Delete this campaign? Drafts will be unlinked as standalone posts.`)) return;
              try {
                await api.delete(`/campaigns/${campaign.id}`);
                toast.success('Campaign deleted');
                onDeleted?.();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Delete failed');
              }
            }}
            data-testid="campaign-detail-delete"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            Delete campaign
          </Button>
          <Button
            size="sm"
            onClick={() => campaign && navigate(`/posts?campaign=${campaign.id}`)}
            disabled={!campaign}
            data-testid="campaign-detail-open-posts"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1" />
            Open all posts
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children, span }) => (
  <div className={span ? 'sm:col-span-2' : ''}>
    <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-0.5">{label}</p>
    <div className="text-sm text-zinc-800 break-words">{children}</div>
  </div>
);

const Stat = ({ label, value, tint }) => (
  <div className={`rounded border border-zinc-100 p-2 ${tint}`}>
    <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
    <p className="text-lg font-semibold">{value || 0}</p>
  </div>
);

export default CampaignsPage;
